import { useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import QuickAppointmentModal from 'src/features/appointments/QuickAppointmentModal';
import type { Appointment } from 'src/features/appointments/types';
import { isManager } from 'src/features/auth/roles';
import { useBusiness, useSession } from 'src/features/auth/useAuth';
import ClientFormModal from 'src/features/clients/ClientFormModal';
import QuickExpense from 'src/features/finance/QuickExpense';
import ChargeSheet from 'src/features/payments/ChargeSheet';
import useQuickAction from 'src/shared/hooks/useQuickAction';
import { formatDayShort, formatTime, zonedDateKey } from 'src/shared/utils/datetime';
import { notify } from 'src/shared/utils/notify';

/**
 * Mounts the dialog the URL asks for (`?accion=…`) over whatever page is open.
 *
 * The one place these flows start from, whichever button asked: the phone's
 * "+", the sidebar, the Inicio shortcuts, a client's profile. The shell
 * imports the feature dialogs the same way it imports their nav entries.
 */
export default function QuickActionHost() {
  const { action, extra, close } = useQuickAction();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const booked = useRef<Appointment | null>(null);
  const user = useSession();
  const { data: business } = useBusiness();
  const timeZone = business?.timezone;

  switch (action) {
    case 'nueva-cita': {
      // Booked from the agenda: land on the day and open what was just booked,
      // in place of the dialog's history entry. Anywhere else, a toast offers
      // the way there without taking the user off the page they were on.
      const finish = () => {
        const appointment = booked.current;
        booked.current = null;
        if (appointment && pathname === '/agenda') {
          const day = zonedDateKey(appointment.startsAt, timeZone);
          void navigate(`/agenda?dia=${day}&cita=${appointment.id}`, { replace: true });
          return;
        }
        close();
      };
      const clientId = Number(extra('clienta'));
      return (
        <QuickAppointmentModal
          isOpen
          toggle={finish}
          timeZone={timeZone}
          currency={business?.currency}
          mayOverride={isManager(user?.role)}
          defaultDate={extra('fecha')}
          defaultClientId={Number.isFinite(clientId) && clientId > 0 ? clientId : undefined}
          onBooked={(appointment) => {
            booked.current = appointment;
            const day = zonedDateKey(appointment.startsAt, timeZone);
            const when = `${formatDayShort(day)}, ${formatTime(appointment.startsAt, timeZone)}`;
            if (pathname === '/agenda') {
              notify.success(`Cita agendada · ${when}`);
              return;
            }
            notify.success(`Cita agendada · ${when}`, {
              label: 'Ver',
              onClick: () => void navigate(`/agenda?dia=${day}&cita=${appointment.id}`),
            });
          }}
        />
      );
    }
    case 'nueva-clienta':
      return (
        <ClientFormModal
          isOpen
          toggle={close}
          onSaved={(client) => void navigate(`/clientes/${client.id}`, { replace: true })}
        />
      );
    case 'cobrar':
      return <ChargeSheet toggle={close} />;
    case 'nuevo-gasto':
      // Mirrors `RESOURCE_ROLES["expenses:manage"]`.
      return isManager(user?.role) ? <QuickExpense toggle={close} /> : null;
    default:
      return null;
  }
}
